import {
  buildMfaTwoFactorFromSession,
  createInitialTwoFactorState,
  logAuditEvent,
  useForceLogout,
  validateJwtOnLoad,
  type MfaTotpEnrollResult,
} from "@dwelldrop/shared";
import { getSupabase } from "@dwelldrop/supabase";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  type AuthState,
  type PendingSession,
  type RateLimitState,
  type VenueProfile,
  buildSignedOutState,
  handleNavigationSessionCheck,
  handleVisibilitySessionCheck,
  processAuthStateChange,
  signOutAllVenueDevices,
  signOutVenueUser,
  VENUE_TOKEN_KEY,
} from "../services/context";
import {
  createVenueMfaHandlers,
  venueComputeMfaLockedOut,
  venueSignInWithPassword,
} from "../services/venueAuthMfa";

interface VenueAuthContextValue extends AuthState {
  signIn: (
    email: string,
    password: string,
    rememberMe: boolean
  ) => Promise<{
    error: string | null;
    needs2FA: boolean;
    retryAfterSeconds?: number;
  }>;
  verify2FA: (code: string) => Promise<{ error: string | null }>;
  enable2FAEnrollTotp: () => Promise<MfaTotpEnrollResult>;
  enable2FAVerify: (params: {
    code: string;
    factorId: string;
    challengeId?: string;
    phone?: string;
  }) => Promise<{ error: string | null; recoveryCodes?: string[] }>;
  disable2FA: () => Promise<{ error: string | null }>;
  disable2FASendChallenge: () => Promise<{
    error: string | null;
    factorId: string | null;
    challengeId: string | null;
  }>;
  disable2FAVerify: (params: {
    code: string;
    factorId: string;
    challengeId: string;
  }) => Promise<{ error: string | null }>;
  generateRecoveryCodes: () => Promise<{
    error: string | null;
    recoveryCodes: string[];
  }>;
  verifyRecoveryCode: (code: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  signOutAllDevices: () => Promise<void>;
  rateLimit: RateLimitState;
  isLockedOut: () => boolean;
}

const VenueAuthContext = createContext<VenueAuthContextValue | null>(null);

export function useVenueAuth(): VenueAuthContextValue {
  const ctx = useContext(VenueAuthContext);
  if (!ctx)
    throw new Error("useVenueAuth must be used within VenueAuthProvider");
  return ctx;
}

export function VenueAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    profile: null,
    twoFactor: createInitialTwoFactorState(),
    sessionExpired: false,
  });
  const profileRef = useRef<VenueProfile | null>(null);
  useEffect(() => {
    profileRef.current = state.profile;
  }, [state.profile]);
  const fetchInitiated = useRef(false);
  const signInInProgress = useRef(false);
  const isSigningOutRef = useRef(false);
  const pendingMfaVerificationRef = useRef(false);
  const mfaCompletingRef = useRef(false);

  useEffect(() => {
    pendingMfaVerificationRef.current = state.twoFactor.pendingVerification;
  }, [state.twoFactor.pendingVerification]);
  const [rateLimit, setRateLimit] = useState<RateLimitState>({
    smsSendCount: 0,
    smsSendWindowStart: 0,
    failedAttempts: 0,
    lockedUntil: null,
  });
  const [pendingSession, setPendingSession] = useState<PendingSession | null>(
    null
  );
  const isLockedOut = useCallback(() => {
    return venueComputeMfaLockedOut(rateLimit.lockedUntil, setRateLimit);
  }, [rateLimit.lockedUntil]);

  const mfaHandlers = useMemo(
    () =>
      createVenueMfaHandlers({
        pendingSession,
        rateLimit,
        setRateLimit,
        setPendingSession,
        setState,
        isLockedOut,
        twoFactor: state.twoFactor,
        userId: state.user?.id ?? null,
        mfaCompletingRef,
        pendingMfaVerificationRef,
      }),
    [
      pendingSession,
      rateLimit,
      isLockedOut,
      state.twoFactor,
      state.user?.id,
    ]
  );

  useEffect(() => {
    let supabase;
    try {
      supabase = getSupabase(VENUE_TOKEN_KEY);
    } catch {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    let cancelled = false;
    let initialSessionResolved = false;

    validateJwtOnLoad(supabase, "/login", async () => {
      await logAuditEvent(
        supabase,
        "jwt_tamper_detected",
        null,
        {},
        "auth",
        "/login"
      );
    });

    const timeout = setTimeout(() => {
      console.warn(
        "[VenueAuth] Session bootstrap did not finish within 5s — forcing loading=false"
      );
      setState((s) => (s.loading ? { ...s, loading: false } : s));
    }, 5000);

    const authDeps = {
      setState,
      profileRef,
      fetchInitiated,
      signInInProgress,
      isSigningOutRef,
      pendingMfaVerificationRef,
      mfaCompletingRef,
      setPendingSession,
      rateLimit,
      setRateLimit,
    };

    void supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      clearTimeout(timeout);
      initialSessionResolved = true;

      if (session?.user) {
        if (
          pendingMfaVerificationRef.current ||
          signInInProgress.current ||
          mfaCompletingRef.current
        ) {
          setState((s) => ({ ...s, loading: false }));
          return;
        }

        const bits = await buildMfaTwoFactorFromSession(
          VENUE_TOKEN_KEY,
          null
        );
        if (bits.pendingVerification) {
          await supabase.auth.signOut();
          if (cancelled) return;
          setState(buildSignedOutState());
          setPendingSession(null);
          return;
        }

        await processAuthStateChange("INITIAL_SESSION", session, authDeps);
      } else {
        setState(buildSignedOutState());
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!initialSessionResolved || cancelled) return;
      if (event === "INITIAL_SESSION") return;
      clearTimeout(timeout);
      await processAuthStateChange(event, session, authDeps);
    });

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        const expired = await handleVisibilitySessionCheck(profileRef.current);
        if (expired) return;
        await supabase.auth.getSession();
      } else {
        supabase.auth.stopAutoRefresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const location = useLocation();
  useEffect(() => {
    void handleNavigationSessionCheck(profileRef.current);
  }, [location]);

  const signIn = useCallback(
    async (email: string, password: string, rememberMe: boolean) => {
      pendingMfaVerificationRef.current = true;
      const result = await venueSignInWithPassword({
        email,
        password,
        rememberMe,
        rateLimit,
        setRateLimit,
        isLockedOut,
        setPendingSession,
        setState,
        signInInProgress,
      });
      if (!result.needs2FA) {
        pendingMfaVerificationRef.current = false;
      }
      return result;
    },
    [isLockedOut, rateLimit]
  );

  const signOut = useCallback(
    async () => {
      pendingMfaVerificationRef.current = false;
      return signOutVenueUser(
        state.user?.id ?? null,
        isSigningOutRef,
        setState,
        setPendingSession
      );
    },
    [state.user]
  );

  const supabaseForLogout = state.user
    ? getSupabase(VENUE_TOKEN_KEY)
    : null;
  const { broadcast: broadcastForceLogout } = useForceLogout(
    supabaseForLogout,
    state.user?.id ?? null,
    signOut
  );
  const signOutAllDevices = useCallback(
    async () =>
      signOutAllVenueDevices(
        state.user?.id ?? null,
        isSigningOutRef,
        broadcastForceLogout,
        setState,
        setPendingSession
      ),
    [state.user, broadcastForceLogout]
  );

  return (
    <VenueAuthContext.Provider
      value={{
        ...state,
        signIn,
        verify2FA: mfaHandlers.verify2FA,
        enable2FAEnrollTotp: mfaHandlers.enable2FAEnrollTotp,
        enable2FAVerify: mfaHandlers.enable2FAVerify,
        disable2FA: mfaHandlers.disable2FA,
        disable2FASendChallenge: mfaHandlers.disable2FASendChallenge,
        disable2FAVerify: mfaHandlers.disable2FAVerify,
        generateRecoveryCodes: mfaHandlers.generateRecoveryCodes,
        verifyRecoveryCode: mfaHandlers.verifyRecoveryCode,
        signOut,
        signOutAllDevices,
        rateLimit,
        isLockedOut,
      }}
    >
      {children}
    </VenueAuthContext.Provider>
  );
}
